// ============================================================================
// MarketMasterReviewCard — карточка мастера на проверке (без таблиц/окон).
// Показывает заявку мастера, последнюю причину отказа и приложенные документы;
// решение — одобрить или отклонить с обязательной причиной.
// ============================================================================

import { useState } from 'react';
import {
  Check,
  Download,
  FileText,
  Loader2,
  Mail,
  Phone,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MarketRatingBadge } from './MarketRatingBadge';
import { useToastStore } from '@/store/toastStore';
import { logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import {
  downloadMarketAttachment,
  formatAttachmentSize,
  type MarketAttachment,
  type MarketMasterReview,
  type MasterReviewDecision,
} from '@/lib/marketOperator';

interface MarketMasterReviewCardProps {
  master: MarketMasterReview;
  documents: MarketAttachment[];
  busy: boolean;
  onReview: (master: MarketMasterReview, decision: MasterReviewDecision, reason: string) => void;
}

export function MarketMasterReviewCard({
  master,
  documents,
  busy,
  onReview,
}: MarketMasterReviewCardProps) {
  const showError = useToastStore((s) => s.showError);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState(master.reason);

  const created = formatValue(master.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const decided = formatValue(master.decidedAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');

  const download = async (file: MarketAttachment) => {
    try {
      await downloadMarketAttachment(file);
    } catch (e) {
      logErrorToJournal(e, 'Оператор маркетплейса: скачивание документа мастера');
      showError(`Не удалось скачать «${file.fileName}»`);
    }
  };

  const approve = () => onReview(master, 'approve', '');
  const reject = () => {
    if (!reason.trim()) {
      showError('Укажите причину отказа');
      return;
    }
    onReview(master, 'reject', reason.trim());
    setRejecting(false);
  };

  return (
    <article className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="break-words text-sm font-semibold leading-5">{master.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            {master.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {master.phone}
              </span>
            )}
            {master.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {master.email}
              </span>
            )}
            {created && <span>Заявка: {created}</span>}
            <MarketRatingBadge rating={master.rating} count={master.reviewsCount} compact />
          </div>
        </div>
        <span
          className={
            master.state === 'rejected'
              ? 'shrink-0 rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive'
              : 'shrink-0 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700'
          }
        >
          {master.state === 'rejected' ? 'Отклонён' : 'На проверке'}
        </span>
      </div>

      {master.about && (
        <p className="mt-3 whitespace-pre-wrap break-words text-xs leading-5 text-muted-foreground">
          {master.about}
        </p>
      )}

      {master.state === 'rejected' && master.reason && (
        <div className="mt-3 rounded-xl border border-destructive/25 bg-destructive/5 px-3 py-2.5">
          <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-destructive">
            <ShieldAlert className="h-3.5 w-3.5" />
            Причина отказа
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-foreground">
            {master.reason}
          </div>
          {(master.decidedByName || decided) && (
            <div className="mt-1 text-[11px] text-muted-foreground">
              {master.decidedByName && <>Решение: {master.decidedByName}</>}
              {master.decidedByName && decided && ' · '}
              {decided}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 rounded-xl border bg-muted/30 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <FileText className="h-3.5 w-3.5" />
          Документы
          {documents.length > 0 && <span className="normal-case">· {documents.length}</span>}
        </div>
        {documents.length === 0 ? (
          <div className="mt-1 text-xs leading-5 text-muted-foreground">
            Документы не приложены — проверьте профиль мастера.
          </div>
        ) : (
          <div className="mt-2 space-y-1.5">
            {documents.map((file) => (
              <div key={file.id} className="flex items-center gap-2 text-xs">
                <span className="min-w-0 flex-1 truncate" title={file.fileName}>
                  {file.fileName}
                </span>
                {formatAttachmentSize(file.fileSize) && (
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {formatAttachmentSize(file.fileSize)}
                  </span>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 rounded-lg px-2 text-[11px]"
                  onClick={() => void download(file)}
                >
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejecting ? (
        <div className="mt-3 space-y-2">
          <label className="block text-[11px] font-medium text-muted-foreground" htmlFor={`reject-${master.id}`}>
            Причина отказа <span className="text-destructive">*</span>
          </label>
          <textarea
            id={`reject-${master.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Что не так с профилем мастера"
            className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            disabled={busy}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 flex-1 rounded-xl"
              onClick={() => setRejecting(false)}
              disabled={busy}
            >
              Отмена
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="h-10 flex-1 rounded-xl"
              onClick={reject}
              disabled={busy || !reason.trim()}
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Отклонить
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-10 flex-1 rounded-xl text-destructive hover:text-destructive"
            onClick={() => setRejecting(true)}
            disabled={busy}
          >
            <ShieldAlert className="mr-1.5 h-4 w-4" />
            Отклонить
          </Button>
          <Button
            type="button"
            className="h-10 flex-1 rounded-xl"
            onClick={approve}
            disabled={busy}
          >
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
            Одобрить
          </Button>
        </div>
      )}

      {busy && !rejecting && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Check className="h-3.5 w-3.5" />
          Сохраняем решение…
        </div>
      )}
    </article>
  );
}
